import React, { useState } from 'react';
import {
  Bell, Smartphone, Clock,
  Check, ChevronDown, ArrowRight, Save
} from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const HALF_HOURS = [];
for (let i = 0; i < 24; i++) {
  for (const m of ['00', '30']) {
    const h = i % 12 || 12;
    const ampm = i < 12 ? 'AM' : 'PM';
    HALF_HOURS.push({ value: `${i}:${m}`, label: `${h}:${m} ${ampm}` });
  }
}

const Toggle = ({ checked, onChange, id }) => (
  <label className="al-toggle" htmlFor={id}>
    <input type="checkbox" id={id} checked={checked} onChange={e => onChange(e.target.checked)} />
    <span className="al-toggle__track"><span className="al-toggle__thumb" /></span>
  </label>
);

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const TABS = [
  { id: 'setup', label: 'Setup', icon: Smartphone },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'schedule', label: 'Schedule', icon: Clock },
];

const AlertsPage = () => {
  const [tab, setTab] = useState('setup');
  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [alerts, setAlerts] = useState({
    tradeSignals: true,
    entryAlerts: true,
    exitAlerts: true,
    stopLossHit: true,
    dailySummary: false,
    weeklyReport: false,
  });
  const [schedule, setSchedule] = useState({
    startTime: '9:00',
    endTime: '16:00',
    activeDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    quietHours: false,
    quietStart: '22:00',
    quietEnd: '7:00',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
    setPhoneVerified(false);
    setCodeSent(false);
    setCode('');
  };

  const handleSendCode = async () => {
    setCodeSent(true);
  };

  const handleVerifyCode = async () => {
    if (code.length < 5) return;
    setVerifying(true);
    await new Promise(r => setTimeout(r, 1200));
    setVerifying(false);
    setPhoneVerified(true);
    setTimeout(() => setTab('notifications'), 600);
  };

  const handleCodeInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
    setCode(digits);
  };

  const toggle = (key) => {
    setAlerts(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDay = (day) => {
    setSchedule(prev => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter(d => d !== day)
        : [...prev.activeDays, day],
    }));
  };


  const phoneDigits = phone.replace(/\D/g, '');
  const enabledCount = Object.values(alerts).filter(Boolean).length;

  return (
    <div className="page">
      <div className="al-page">
        {/* Header */}
        <div className="al-page__header">
          <div className="al-page__header-text">
            <h1 className="al-page__title">Alerts</h1>
            <p className="al-page__subtitle">SMS notifications for live trades and performance</p>
          </div>
        </div>

        {/* Main panel */}
        <div className="al-panel">
          {/* Tabs */}
          <div className="al-tabs">
            {TABS.map(({ id, label, icon: Icon }) => {
              const locked = !phoneVerified && id !== 'setup';
              return (
                <button
                  key={id}
                  className={`al-tab ${tab === id ? 'al-tab--active' : ''} ${locked ? 'al-tab--locked' : ''}`}
                  onClick={() => { if (!locked) setTab(id); }}
                  title={locked ? 'Verify your phone number first' : ''}
                >
                  <Icon size={15} />
                  <span>{label}</span>
                  {id === 'notifications' && phoneVerified && <span className="al-tab__badge">{enabledCount}</span>}
                  {locked && <span className="al-tab__lock">🔒</span>}
                </button>
              );
            })}
          </div>

          {/* Setup tab */}
          {tab === 'setup' && (
            <div className="al-content">
              <div className="al-section__head">
                <span className="al-section__title">Phone Number</span>
                <span className="al-section__hint">We'll send a 5-digit code to verify your number</span>
              </div>

              {/* Phone input */}
              <div className="al-phone">
                <div className="al-phone__prefix">
                  <span>🇺🇸</span>
                  <span>+1</span>
                  <ChevronDown size={12} />
                </div>
                <input
                  type="tel"
                  className="al-phone__input"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={handlePhoneChange}
                  disabled={phoneVerified}
                />
                {phoneVerified && (
                  <span className="al-phone__verified"><Check size={13} /> Verified</span>
                )}
              </div>

              {/* Send code button */}
              {phoneDigits.length === 10 && !codeSent && !phoneVerified && (
                <button className="al-send-code" onClick={handleSendCode}>
                  Send Verification Code
                </button>
              )}

              {/* Code input */}
              {codeSent && !phoneVerified && (
                <div className="al-code">
                  <p className="al-code__sent">Code sent to +1 {phone}</p>
                  <div className="al-code__row">
                    <input
                      type="text"
                      className="al-code__input"
                      placeholder="00000"
                      value={code}
                      onChange={handleCodeInput}
                      maxLength={5}
                      autoFocus
                    />
                    <button
                      className="al-code__verify"
                      onClick={handleVerifyCode}
                      disabled={code.length < 5 || verifying}
                    >
                      {verifying ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>
                  <button className="al-code__resend" onClick={handleSendCode}>
                    Resend code
                  </button>
                </div>
              )}

              {/* Hints */}
              {phoneVerified && (
                <div className="al-status">
                  <div className="al-status__dot" />
                  <span>Your number is verified and ready to receive alerts</span>
                </div>
              )}
              {!phoneVerified && !codeSent && phoneDigits.length > 0 && phoneDigits.length < 10 && (
                <div className="al-status al-status--pending">
                  <span>Enter a valid 10-digit number</span>
                </div>
              )}

              {phoneVerified && (
                <div className="al-footer">
                  <button className="al-footer__btn al-footer__btn--primary" onClick={() => setTab('notifications')}>
                    Next: Notifications <ArrowRight size={15} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Notifications tab */}
          {tab === 'notifications' && (
            <div className="al-content">
              <div className="al-group">
                <span className="al-group__label">Trade Alerts</span>
                <div className="al-row">
                  <div className="al-row__text">
                    <span className="al-row__name">All trade signals</span>
                    <span className="al-row__desc">Every buy and sell signal from the algorithm</span>
                  </div>
                  <Toggle checked={alerts.tradeSignals} onChange={() => toggle('tradeSignals')} id="tradeSignals" />
                </div>
                <div className="al-row">
                  <div className="al-row__text">
                    <span className="al-row__name">Entry alerts</span>
                    <span className="al-row__desc">When the algorithm enters a new position</span>
                  </div>
                  <Toggle checked={alerts.entryAlerts} onChange={() => toggle('entryAlerts')} id="entryAlerts" />
                </div>
                <div className="al-row">
                  <div className="al-row__text">
                    <span className="al-row__name">Exit alerts</span>
                    <span className="al-row__desc">When a position is closed</span>
                  </div>
                  <Toggle checked={alerts.exitAlerts} onChange={() => toggle('exitAlerts')} id="exitAlerts" />
                </div>
                <div className="al-row">
                  <div className="al-row__text">
                    <span className="al-row__name">Stop loss triggered</span>
                    <span className="al-row__desc">Instant notification when a stop loss is hit</span>
                  </div>
                  <Toggle checked={alerts.stopLossHit} onChange={() => toggle('stopLossHit')} id="stopLossHit" />
                </div>
              </div>

              <div className="al-group">
                <span className="al-group__label">Summaries</span>
                <div className="al-row">
                  <div className="al-row__text">
                    <span className="al-row__name">Daily P&L summary</span>
                    <span className="al-row__desc">End-of-day performance recap at market close</span>
                  </div>
                  <Toggle checked={alerts.dailySummary} onChange={() => toggle('dailySummary')} id="dailySummary" />
                </div>
                <div className="al-row">
                  <div className="al-row__text">
                    <span className="al-row__name">Weekly report</span>
                    <span className="al-row__desc">Win rate, total P&L, and top trades from the week</span>
                  </div>
                  <Toggle checked={alerts.weeklyReport} onChange={() => toggle('weeklyReport')} id="weeklyReport" />
                </div>
              </div>

              <div className="al-footer">
                <button className="al-footer__btn al-footer__btn--ghost" onClick={() => setTab('setup')}>
                  Back
                </button>
                <button className="al-footer__btn al-footer__btn--primary" onClick={() => setTab('schedule')}>
                  Next: Schedule <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* Schedule tab */}
          {tab === 'schedule' && (
            <div className="al-content">
              <div className="al-schedule">
                <div className="al-schedule__block">
                  <span className="al-schedule__label">Active hours</span>
                  <div className="al-schedule__times">
                    <select
                      className="al-select"
                      value={schedule.startTime}
                      onChange={e => { setSchedule(prev => ({ ...prev, startTime: e.target.value })); }}
                    >
                      {HALF_HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                    </select>
                    <span className="al-schedule__sep">—</span>
                    <select
                      className="al-select"
                      value={schedule.endTime}
                      onChange={e => { setSchedule(prev => ({ ...prev, endTime: e.target.value })); }}
                    >
                      {HALF_HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="al-schedule__block">
                  <span className="al-schedule__label">Active days</span>
                  <div className="al-days">
                    {DAYS.map(day => (
                      <button
                        key={day}
                        className={`al-day ${schedule.activeDays.includes(day) ? 'al-day--on' : ''}`}
                        onClick={() => toggleDay(day)}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="al-divider" />

              <div className="al-row al-row--last">
                <div className="al-row__text">
                  <span className="al-row__name">Quiet hours</span>
                  <span className="al-row__desc">Pause all alerts during off hours</span>
                </div>
                <Toggle
                  checked={schedule.quietHours}
                  onChange={val => { setSchedule(prev => ({ ...prev, quietHours: val })); }}
                  id="quietHours"
                />
              </div>

              {schedule.quietHours && (
                <div className="al-schedule__times al-schedule__times--indent">
                  <select
                    className="al-select"
                    value={schedule.quietStart}
                    onChange={e => { setSchedule(prev => ({ ...prev, quietStart: e.target.value })); }}
                  >
                    {HALF_HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                  <span className="al-schedule__sep">—</span>
                  <select
                    className="al-select"
                    value={schedule.quietEnd}
                    onChange={e => { setSchedule(prev => ({ ...prev, quietEnd: e.target.value })); }}
                  >
                    {HALF_HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                </div>
              )}

              <div className="al-footer">
                <button className="al-footer__btn al-footer__btn--ghost" onClick={() => setTab('notifications')}>
                  Back
                </button>
                <button
                  className={`al-footer__btn al-footer__btn--save ${saved ? 'al-footer__btn--saved' : ''}`}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : saved ? <><Check size={15} /> Saved</> : <><Save size={15} /> Save Preferences</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertsPage;
